import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaStore } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import ListingCard from "./ListingCard";
import Skeleton from "react-loading-skeleton";
import { trackMarketplaceEvent } from "../../utils/marketplaceAnalytics";
import { filterMarketplaceListings } from "../../utils/marketplaceListingFilters";
import styles from "./MarketplaceListingsSection.module.css";

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== "") {
      searchParams.set(key, String(value));
    }
  });
  return searchParams.toString();
};

const EMPTY_RELATED_IDS = Object.freeze([]);

const parseFilterKey = (filterKey) => {
  const [categoryPart = "", productPart = ""] = String(filterKey || "").split("|");
  return {
    relatedCategoryIds: categoryPart
      ? categoryPart.split(",").filter(Boolean)
      : EMPTY_RELATED_IDS,
    relatedProductIds: productPart
      ? productPart.split(",").filter(Boolean)
      : EMPTY_RELATED_IDS,
  };
};

const MarketplaceListingsSection = ({
  title = "Also on Marketplace",
  queryParams = {},
  limit = 4,
  compact = true,
  viewAllHref,
  viewAllLabel = "Browse marketplace →",
  widgetSource = "generic",
  className = "",
  variant = "default",
  relatedCategoryIds = EMPTY_RELATED_IDS,
  relatedProductIds = EMPTY_RELATED_IDS,
  showMatchScore = true,
}) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackedRef = useRef(false);
  const sectionRef = useRef(null);

  const queryKey = buildQueryString({
    ...queryParams,
    limit: String(Math.max(limit * 3, limit)),
  });
  const filterKey = `${relatedCategoryIds.join(",")}|${relatedProductIds.join(",")}`;

  useEffect(() => {
    let cancelled = false;
    const { relatedCategoryIds: categoryIds, relatedProductIds: productIds } =
      parseFilterKey(filterKey);

    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams(queryKey);
        if (!params.get("sort")) params.set("sort", "recommended");
        const res = await api.get(`/listings?${params.toString()}`);
        if (!cancelled) {
          const raw = Array.isArray(res.data?.listings) ? res.data.listings : [];
          setListings(
            filterMarketplaceListings(raw, {
              relatedCategoryIds: categoryIds,
              relatedProductIds: productIds,
            }),
          );
        }
      } catch {
        if (!cancelled) setListings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchListings();
    return () => {
      cancelled = true;
    };
  }, [queryKey, filterKey]);

  useEffect(() => {
    if (loading || listings.length === 0 || trackedRef.current) return;
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      trackedRef.current = true;
      trackMarketplaceEvent("marketplace_widget_impression", {
        widgetSource,
        count: listings.length,
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !trackedRef.current) {
          trackedRef.current = true;
          trackMarketplaceEvent("marketplace_widget_impression", {
            widgetSource,
            count: listings.length,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, listings.length, widgetSource]);

  if (!loading && listings.length === 0) return null;

  const browseHref =
    viewAllHref || `/marketplace?${buildQueryString(queryParams)}`;

  const sectionClassName =
    variant === "hero"
      ? `${styles.section} ${styles.sectionHero} ${className}`.trim()
      : `${styles.section} ${className}`.trim();
  const gridClassName =
    variant === "hero" ? `${styles.grid} ${styles.gridHero}` : styles.grid;

  return (
    <section
      ref={sectionRef}
      className={sectionClassName}
      aria-label={title}
    >
      <header className={styles.header}>
        <FaStore className={styles.icon} aria-hidden />
        <h3 className={styles.title}>{title}</h3>
      </header>

      {loading ? (
        <div className={gridClassName}>
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className={styles.skeleton}>
              <Skeleton height={compact ? 100 : 140} />
              <Skeleton height={14} width="80%" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className={gridClassName}>
            {listings.slice(0, limit).map((listing) => (
              <ListingCard
                key={listing._id}
                listing={listing}
                viewMode="grid"
                size={compact ? "compact" : "default"}
                showMatchScore={showMatchScore}
                preferCatalogImage={widgetSource === "feed_matches"}
              />
            ))}
          </div>
          <Link
            to={browseHref}
            className={styles.viewAll}
            onClick={() =>
              trackMarketplaceEvent("marketplace_widget_click", {
                widgetSource,
                target: browseHref,
              })
            }
          >
            {viewAllLabel}
          </Link>
        </>
      )}
    </section>
  );
};

export default MarketplaceListingsSection;
